import React, { useState, useEffect } from 'react';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { auth } from '../firebase';

const LOGO_STX = 'https://www.supportedtravelx.com.au/wp-content/uploads/STX-Logo-Transparent-min-1024x434-1.png';

const STRENGTH_LEVELS = [
  { label: '',            bar: 'bg-gray-200',   text: '' },
  { label: 'Weak',        bar: 'bg-red-500',     text: 'text-red-600' },
  { label: 'Fair',        bar: 'bg-orange-400',  text: 'text-orange-500' },
  { label: 'Good',        bar: 'bg-yellow-500',  text: 'text-yellow-600' },
  { label: 'Strong',      bar: 'bg-green-500',   text: 'text-green-600' },
  { label: 'Very strong', bar: 'bg-green-600',   text: 'text-green-700' },
];

function getStrength(pwd) {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 6)           score++;
  if (pwd.length >= 10)          score++;
  if (/[A-Z]/.test(pwd))         score++;
  if (/[0-9]/.test(pwd))         score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return Math.min(score, 5);
}

const inp = 'w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 placeholder:text-gray-500 focus:border-transparent';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');

  const [verifying,  setVerifying]  = useState(true);
  const [email,      setEmail]      = useState('');
  const [linkError,  setLinkError]  = useState('');

  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [showPwd,    setShowPwd]    = useState(false);
  const [showCfm,    setShowCfm]    = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError,  setFormError]  = useState('');
  const [done,       setDone]       = useState(false);

  useEffect(() => { document.title = 'Set Password — STX Connect'; }, []);

  useEffect(() => {
    if (!oobCode) {
      setLinkError('This link is invalid. Please request a new one from the login page.');
      setVerifying(false);
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then(resolvedEmail => { setEmail(resolvedEmail); setVerifying(false); })
      .catch(() => {
        setLinkError('This link has expired or already been used. Please request a new one from the login page.');
        setVerifying(false);
      });
  }, [oobCode]);

  const strength = getStrength(password);
  const matches  = !!(password && confirm && password === confirm);
  const mismatch = !!(confirm && password !== confirm);

  async function handleSubmit(e) {
    e.preventDefault();
    if (mismatch || !matches) { setFormError('Passwords do not match.'); return; }
    if (password.length < 6)  { setFormError('Password must be at least 6 characters.'); return; }
    setFormError('');
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
    } catch (err) {
      if (err.code === 'auth/weak-password') {
        setFormError('Password is too weak. Please choose a stronger one.');
      } else if (err.code === 'auth/expired-action-code') {
        setFormError('This link has expired. Please request a new one from the login page.');
      } else {
        setFormError('Something went wrong. Please try again or request a new reset link.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-sm">

        <div className="flex justify-center mb-10">
          <img src={LOGO_STX} alt="STX" className="h-10 object-contain" />
        </div>

        {verifying && (
          <p className="text-center text-gray-500 text-sm">Verifying link…</p>
        )}

        {!verifying && linkError && (
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 text-2xl">✕</div>
            <p className="text-gray-900 font-semibold mb-2">Link unavailable</p>
            <p className="text-sm text-gray-700 mb-6">{linkError}</p>
            <Link
              to="/login"
              className="inline-block bg-teal-600 text-white py-3 px-8 rounded-xl text-sm font-semibold hover:bg-teal-700 transition-colors"
            >
              Go to STX Connect →
            </Link>
            <p className="text-xs text-gray-700 mt-5 leading-relaxed">
              To get a new link, choose <span className="font-medium">Forgot password</span> on the
              sign-in page and we'll email you a fresh one.
            </p>
          </div>
        )}

        {!verifying && !linkError && done && (
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4 text-2xl">✓</div>
            <p className="text-gray-900 font-semibold mb-2">Password set</p>
            <p className="text-sm text-gray-700 mb-6">You can now sign in with your new password.</p>
            <Link
              to="/login"
              className="inline-block bg-teal-600 text-white py-3 px-8 rounded-xl text-sm font-semibold hover:bg-teal-700 transition-colors"
            >
              Go to sign in →
            </Link>
          </div>
        )}

        {!verifying && !linkError && !done && (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Set your password</h2>
            <p className="text-sm text-gray-700 mb-6">
              Account: <span className="font-medium text-gray-900">{email}</span>
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>

              {/* New password */}
              <div>
                <label htmlFor="rp-password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  New password
                </label>
                <div className="relative">
                  <input
                    id="rp-password"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    aria-required="true"
                    aria-describedby="rp-strength"
                    autoComplete="new-password"
                    className={`${inp} pr-11`}
                    placeholder="Choose a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(v => !v)}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPwd
                      ? <EyeOff size={16} aria-hidden="true" />
                      : <Eye    size={16} aria-hidden="true" />}
                  </button>
                </div>

                {/* Strength meter */}
                {password && (
                  <div id="rp-strength" className="mt-2">
                    <div
                      className="flex gap-1 mb-1"
                      role="img"
                      aria-label={`Password strength: ${STRENGTH_LEVELS[strength].label}`}
                    >
                      {[1, 2, 3, 4, 5].map(i => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${
                            i <= strength ? STRENGTH_LEVELS[strength].bar : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <p className={`text-xs font-medium ${STRENGTH_LEVELS[strength].text}`}>
                      {STRENGTH_LEVELS[strength].label}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label htmlFor="rp-confirm" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    id="rp-confirm"
                    type={showCfm ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    aria-required="true"
                    aria-invalid={mismatch}
                    aria-describedby={mismatch ? 'rp-mismatch' : matches ? 'rp-match' : undefined}
                    autoComplete="new-password"
                    className={`${inp} pr-11 ${
                      mismatch ? 'border-red-400 focus:ring-red-400' :
                      matches  ? 'border-green-400 focus:ring-green-400' : ''
                    }`}
                    placeholder="Re-enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCfm(v => !v)}
                    aria-label={showCfm ? 'Hide confirm password' : 'Show confirm password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCfm
                      ? <EyeOff size={16} aria-hidden="true" />
                      : <Eye    size={16} aria-hidden="true" />}
                  </button>
                </div>

                {/* Match indicator */}
                {confirm && (
                  <div className="mt-1.5 flex items-center gap-1.5" aria-live="polite" aria-atomic="true">
                    {matches ? (
                      <>
                        <CheckCircle size={13} className="text-green-500 flex-shrink-0" aria-hidden="true" />
                        <span id="rp-match" className="text-xs text-green-600 font-medium">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={13} className="text-red-500 flex-shrink-0" aria-hidden="true" />
                        <span id="rp-mismatch" className="text-xs text-red-600 font-medium">Passwords do not match</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Form error */}
              <div aria-live="assertive" aria-atomic="true">
                {formError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <p role="alert" className="text-red-700 text-sm">{formError}</p>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting || !password || !confirm || mismatch}
                className="w-full bg-teal-600 text-white py-3 rounded-xl text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Setting password…' : 'Set password'}
              </button>

              <p className="text-center text-sm">
                <Link to="/login" className="text-teal-600 font-medium hover:underline">
                  ← Back to sign in
                </Link>
              </p>

            </form>
          </>
        )}
      </div>
    </div>
  );
}
